import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Section";
import { LineupShowcase } from "@/components/LineupShowcase";
import ZoomImage from "@/components/ZoomImage";
import { BannerBeat, BannerGallery } from "@/components/BannerBeat";
import FounderStory from "@/components/FounderStory";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";

const FOUNDER_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/5xrgjpf0_greg%20pic%20smiling%20July%202026%20.jpg";

const A_HERO = [
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/ivv95lqc_1CA087D9-2A51-4067-A641-A842916C6FAA_1_105_c.jpg",
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/6sfd0imd_2E0CAA53-02E9-4861-B464-4D5C08B22226_1_105_c.jpg",
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/hu8hcl47_E34E1C05-4935-41B3-8A7C-9CBD181EE3A9_1_105_c.jpg",
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/skrkrw4c_FA36DD0D-E02B-4247-9850-A3AD39CCB89F_1_105_c.jpg",
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/4foaakew_Screenshot%202026-08-07%20at%205.42.03%E2%80%AFAM.webp",
];

// Chapter imagery
const IMG_LINEUP_BEACH =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/eqcorxrc_Screenshot%202026-08-07%20at%205.20.06%E2%80%AFAM.jpg";
const IMG_WORN_DRIVING =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/f0cwhjj7_47D6BA67-F3A9-4A69-BA68-D4DE144F85F5_1_105_c.jpg";
const IMG_ROPE_SUNSET =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/10qs5ewa_958DC6CC-B130-492E-AFBE-AB6D5BE1FA6E_1_105_c.jpg";
const IMG_RESERVOIR_SUNSET =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/j1q973f1_6E09BC25-9265-4B2F-8175-8CF4C38FED13_1_105_c.jpg";
const IMG_WORKSHOP =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/1x02yxas_Screenshot%202026-08-07%20at%205.18.29%E2%80%AFAM.webp";
const IMG_VALVE_MACRO =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/1meu3lwa_AA572FD2-B1BC-4685-952F-75855EB97548_1_105_c.jpg";
const IMG_LINEUP_PALM =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/urutdwsw_C58E75AB-6B4D-4EA5-B337-E1811717D96F_1_105_c.jpg";
const IMG_BEACH_SAND =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/c02bqevh_Screenshot%202026-08-07%20at%205.29.12%E2%80%AFAM.webp";
const IMG_BLUE_VALVE =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/65in9li7_Screenshot%202026-08-07%20at%205.25.08%E2%80%AFAM.jpg";

const ABOUT_BEATS = [
  { testId: "beat-access", src: IMG_LINEUP_BEACH, alt: "A colorful lineup of WearSpray wearables on driftwood", position: "top", lines: ["The products already existed.", { t: "The problem was access.", accent: true }], caption: "One platform · many colors, many uses" },
  { testId: "beat-distance", src: IMG_WORN_DRIVING, alt: "A WearSpray band worn while driving", position: "top", lines: [{ t: "WearSpray was designed to remove that distance.", accent: true }], caption: "Already on the wrist · ready in the moment" },
  { testId: "beat-stride", src: IMG_ROPE_SUNSET, alt: "A WearSpray band on rope by the ocean at sunset", position: "top", lines: [{ t: "Confidence without interruption.", accent: true }, { t: "Protection in stride.", accent: true }, { t: "Personal care within reach.", accent: true }], caption: "Wherever the day takes you" },
  { testId: "beat-container", src: IMG_RESERVOIR_SUNSET, alt: "A translucent WearSpray band held to the sunset", position: "top", lines: [{ t: "More than a container, a refillable reservoir.", accent: true }], caption: "Access can change behavior" },
  { testId: "beat-beach", src: IMG_BEACH_SAND, alt: "A blue WearSpray band on golden beach sand", position: "bottom", lines: [{ t: "At home wherever you are.", accent: true }], caption: "Personal care that goes where you go" },
  { testId: "beat-workshop", src: IMG_WORKSHOP, alt: "An early WearSpray prototyping workshop", position: "bottom", lines: [{ t: "What began as a question became an engineering challenge.", accent: false }], caption: "Early prototyping · hand-molded forms and first prints" },
  { testId: "beat-valve", src: IMG_VALVE_MACRO, alt: "Macro detail of the patented WearSpray dispensing valve", position: "bottom", lines: [{ t: "The patented dispensing valve.", accent: true }], caption: "Engineered detail · the heart of the platform" },
  { testId: "beat-measure", src: IMG_BLUE_VALVE, alt: "Macro detail of the illuminated dispensing valve", position: "top", lines: ["Wearables learned to measure us.", { t: "The next generation may help us act.", accent: true }], caption: "Engineered detail · built for what connects next" },
];


// --- Building blocks -------------------------------------------------------

function Chapter({ n, label, dark = false, children, testId }) {
  return (
    <section className={`border-t border-zinc-800 ${dark ? "bg-[#0d0d0d]" : "bg-[#0a0a0a]"}`} data-testid={testId}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <div className="flex items-center gap-4 mb-14">
            <span className="font-mono text-sm text-accent">{n}</span>
            <span className="h-px flex-1 max-w-[60px] bg-zinc-700" />
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">{label}</span>
          </div>
        </Reveal>
        <div className="max-w-4xl">{children}</div>
      </div>
    </section>
  );
}

function Heading({ children }) {
  return (
    <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.1]">
      {children}
    </h2>
  );
}

function Statement({ children, className = "" }) {
  return (
    <p className={`font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.25] ${className}`}>
      {children}
    </p>
  );
}

function Staccato({ lines, className = "" }) {
  return (
    <div className={`space-y-3 ${className}`}>
      {lines.map((l, i) => {
        const t = typeof l === "string" ? l : l.t;
        const accent = typeof l === "object" && l.accent;
        return (
          <Statement key={`${i}-${t}`} className={accent ? "text-accent" : "text-zinc-300"}>
            {t}
          </Statement>
        );
      })}
    </div>
  );
}

function Body({ children, className = "" }) {
  return <p className={`font-body text-zinc-400 text-lg leading-relaxed ${className}`}>{children}</p>;
}

function ChapterImage({ src, alt, caption, className = "", objectPosition = "center", testId }) {
  return (
    <Reveal delay={0.12}>
      <figure className={`max-w-3xl ${className}`}>
        <div className="relative border border-zinc-800 overflow-hidden bg-black">
          <ZoomImage
            src={src}
            alt={alt}
            caption={caption}
            testId={testId}
            className="w-full h-auto object-cover"
          />
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/25 to-transparent" />
        </div>
        {caption && (
          <figcaption className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-500 mt-4">
            {caption}
          </figcaption>
        )}
      </figure>
    </Reveal>
  );
}



function Chips({ items, testId }) {
  return (
    <div className="flex flex-wrap gap-3 mt-8 md:mt-10" data-testid={testId}>
      {items.map((t) => (
        <span
          key={t}
          className="inline-flex items-center gap-2.5 font-mono text-sm text-zinc-300 border border-zinc-800 bg-[#121212] px-4 py-3 hover:border-zinc-700 transition-colors"
        >
          <span className="w-1.5 h-1.5 bg-[#00e5ff] shrink-0" />
          {t}
        </span>
      ))}
    </div>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-4">
      {items.map((t) => (
        <li key={t} className="font-body text-zinc-300 text-lg leading-relaxed flex items-start gap-3">
          <span className="w-1.5 h-1.5 bg-[#00e5ff] shrink-0 mt-2.5" />
          {t}
        </li>
      ))}
    </ul>
  );
}

function Flow({ steps, testId }) {
  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-x-4 gap-y-4" data-testid={testId}>
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-4">
          <span
            className={`font-heading text-2xl md:text-3xl font-light tracking-tight ${
              i === steps.length - 1 ? "text-accent" : ""
            }`}
          >
            {s}
          </span>
          {i < steps.length - 1 && <ArrowRight size={20} className="text-accent shrink-0" />}
        </div>
      ))}
    </div>
  );
}

function BrandPayoff({ className = "", testId = "about-brand-payoff" }) {
  return (
    <div className={className} data-testid={testId}>
      <span className="block font-heading text-4xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-[0.95]">
        WearSpray
      </span>
      <span className="block text-accent text-xl sm:text-2xl md:text-3xl mt-3 tracking-tight">
        Personal Care, Always Within Reach.
      </span>
    </div>
  );
}

// --- Page ------------------------------------------------------------------

export default function About() {
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setHeroIdx((p) => (p + 1) % A_HERO.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="about-page">
      <SEO {...PAGE_SEO["/about"]} path="/about" />
      {/* HERO, product macro crossfade */}
      <section
        className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-black"
        data-testid="about-hero-section"
      >
        {A_HERO.map((src, i) => (
          <motion.div
            key={src}
            className="absolute inset-0"
            initial={false}
            animate={{ opacity: i === heroIdx ? 1 : 0 }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <img loading="lazy"
              src={src}
              alt="WearSpray wearable"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center", filter: "brightness(1.12) contrast(1.04)" }}
            />
          </motion.div>
        ))}
        <div className="absolute inset-0 bg-black/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center pt-24">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono text-xs tracking-[0.25em] uppercase text-accent mb-6"
            data-testid="about-hero"
          >
            Our Story
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.0] max-w-4xl"
          >
            Inventing a New Category in{" "}
            <span className="text-accent">Personal Care &amp; Active Defense.</span>
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <Staccato
              className="mt-10"
              lines={[
                "No searching.",
                "No stopping.",
                "No forgetting.",
                { t: "Just there when you need it.", accent: true },
              ]}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.46 }}
          >
            <BrandPayoff className="mt-14" testId="about-brand-payoff-hero" />
          </motion.div>
          <div className="flex gap-2 mt-12" data-testid="about-hero-progress">
            {A_HERO.map((src, i) => (
              <span
                key={src}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === heroIdx ? "w-10 bg-accent" : "w-5 bg-white/30"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 01 · WHERE IT BEGAN */}
      <section
        className="relative border-t border-zinc-800 bg-[#0a0a0a] overflow-hidden"
        data-testid="about-idea"
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[110vw] h-[55vh] radial-glow opacity-30 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-accent block mb-6">
              01 · Where it began
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.03]">
              It started with a thought I couldn't shake.
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="font-heading text-xl sm:text-2xl md:text-3xl font-light tracking-tight leading-snug text-zinc-200 mt-8 max-w-3xl">
              What if I could just apply what I need,{" "}
              <span className="text-accent">the instant I need it</span>, no
              bottle, no searching, no pause?
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-10 max-w-3xl">
              During COVID I was sanitizing my hands a hundred times a day. One
              more reach for the bottle, and it hit me, the protection I
              need should already be on me. Not something to remember. Not
              something to dig for. Just there, the moment I move.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-mono text-xs sm:text-sm tracking-[0.25em] uppercase text-zinc-400 mt-10">
              That thought became WearSpray.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE ORIGIN FILM */}
      <section className="relative border-t border-zinc-800 overflow-hidden bg-[#0a0a0a]" data-testid="origin-film">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] h-[70vh] radial-glow opacity-30" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent block mb-6">
                The Origin Film · As Seen On Shark Tank
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
                A cinematic take on where it all began.
              </h2>
              <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6">
                When WearSpray went on Shark Tank, they asked us to put together a
                film about the origins of the product. We thought it would be more
                interesting to skip the ordinary and come up with something
                dramatic and exciting instead.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative mt-14 md:mt-20 max-w-4xl mx-auto">
              <div
                className="absolute -inset-6 blur-3xl opacity-50 pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(0,229,255,0.18), transparent 65%)" }}
              />
              <div className="relative border border-zinc-800 bg-black overflow-hidden shadow-2xl shadow-cyan-500/10">
                <div className="aspect-video w-full">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/tt5BqGY_dV8?si=LV_LKbPW4DQBDOyd"
                    title="WearSpray Origin Film"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    data-testid="origin-film-video"
                  />
                </div>
              </div>
              <p className="font-body text-sm text-zinc-400 mt-6 text-center max-w-xl mx-auto leading-relaxed">
                Written, directed, filmed, edited, and performed by{" "}
                <span className="text-zinc-200">Greg Demirjian</span>, inventor of
                WearSpray, who also created the visual effects, composed the
                original score, and performed every instrument in it.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 02 · THEN THE QUESTION BECAME BIGGER */}
      {/* CONSOLIDATED BANNER GALLERY, all product beats in one place */}
      <BannerGallery beats={ABOUT_BEATS} testId="about-banner-gallery" />

      <Chapter n="02" label="Then the Question Became Bigger" testId="about-bigger-question">
        <Reveal>
          <Heading>
            If sanitizer could be instantly accessible,{" "}
            <span className="text-accent">why stop there?</span>
          </Heading>
        </Reveal>
        <Reveal delay={0.1}>
          <Chips
            testId="about-products-chips"
            items={[
              "Sunscreen",
              "Insect Repellent",
              "Skincare",
              "Fragrance",
              "Lotion",
              "Serum",
              "Body Mist",
              "Protective Solutions",
            ]}
          />
        </Reveal>
        <Reveal delay={0.2}>
          <Body className="mt-10">
            WearSpray grew from the belief that the personal care products we want
            and depend on throughout the day should be available at the moment we
            actually need them.
          </Body>
        </Reveal>
      </Chapter>

      <BannerBeat
        testId="about-banner-access"
        src={IMG_LINEUP_BEACH}
        alt="A colorful lineup of WearSpray wearables arranged on driftwood beside coral and a blue sand dollar"
        position="top"
        lines={[
          "The products already existed.",
          { t: "The problem was access.", accent: true },
        ]}
        caption="One platform · many colors, many uses"
      />


      {/* 03 · THE DISTANCE BETWEEN INTENTION AND ACTION */}
      <Chapter n="03" label="The Distance Between Intention and Action" dark testId="about-distance">
        <Reveal>
          <Statement className="text-white">We often know what we should do.</Statement>
        </Reveal>
        <Reveal delay={0.1}>
          <Chips
            testId="about-should-chips"
            items={[
              "Reapply sunscreen",
              "Use repellent",
              "Sanitize our hands",
              "Moisturize",
              "Refresh our skincare",
              "Apply fragrance",
            ]}
          />
        </Reveal>
        <Reveal delay={0.15}>
          <Statement className="text-zinc-300 mt-14">
            But intention does not always become action.
          </Statement>
          <Staccato
            className="mt-6"
            lines={[
              "The bottle may be at home.",
              "It may be in the car.",
              "It may be buried in a bag.",
              "We may simply forget.",
            ]}
          />
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-16 border-l-2 border-accent pl-6 md:pl-8">
            <Flow testId="about-action-flow" steps={["Think about it.", "Reach.", "Press.", "Apply.", "Keep moving."]} />
          </div>
        </Reveal>
        <Reveal delay={0.24}>
          <Body className="mt-14">
            Life should not have to stop because you need something. WearSpray keeps the
            product with you so the moment can continue.
          </Body>
        </Reveal>
      </Chapter>

      <BannerBeat
        testId="about-banner-distance"
        src={IMG_WORN_DRIVING}
        alt="A WearSpray band worn on the wrist while driving, ready in the moment"
        position="top"
        lines={[{ t: "WearSpray was designed to remove that distance.", accent: true }]}
        caption="Already on the wrist · ready in the moment"
      />

      <BannerBeat
        testId="about-banner-stride"
        src={IMG_ROPE_SUNSET}
        alt="A WearSpray band resting on rope by the ocean at sunset"
        position="top"
        lines={[
          { t: "Confidence without interruption.", accent: true },
          { t: "Protection in stride.", accent: true },
          { t: "Personal care within reach.", accent: true },
        ]}
        caption="Wherever the day takes you"
      />


      {/* 04 · MORE THAN A CONTAINER */}
      <Chapter n="04" label="More Than a Container" testId="about-more-than-container">
        <Reveal>
          <Heading>
            WearSpray was never intended to be simply another way to package a
            product.
          </Heading>
        </Reveal>
        <Reveal delay={0.1}>
          <Body className="mt-10">
            It represents a different relationship between people and the personal
            care products they use.
          </Body>
          <Body className="mt-5">
            Instead of carrying the product somewhere nearby, WearSpray brings the
            product directly into the wearable experience.
          </Body>
        </Reveal>
        <Reveal delay={0.15}>
          <Staccato
            className="mt-12"
            lines={[
              "That changes access.",
              "Access can change behavior.",
              { t: "And easier behavior can make reapplication, protection and personal care feel more natural throughout the day.", accent: true },
            ]}
          />
        </Reveal>
      </Chapter>

      <BannerBeat
        testId="about-banner-container"
        src={IMG_RESERVOIR_SUNSET}
        alt="A translucent WearSpray band held to the sunset, revealing the refillable reservoir inside"
        position="top"
        lines={[{ t: "More than a container, a refillable reservoir.", accent: true }]}
        caption="Access can change behavior"
      />

      <BannerBeat
        testId="about-banner-beach"
        src={IMG_BEACH_SAND}
        alt="A blue WearSpray band resting on golden beach sand in the sun"
        position="bottom"
        lines={[{ t: "At home wherever you are.", accent: true }]}
        caption="Personal care that goes where you go"
      />



      {/* LINEUP VISUAL BREAK */}
      <section className="relative border-t border-zinc-800 overflow-hidden bg-black" data-testid="about-lineup-banner">
        <div className="relative h-[360px] md:h-[520px]">
          <LineupShowcase />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40 pointer-events-none" />
        </div>
      </section>

      {/* 05 · FROM AN IDEA TO A PATENTED PLATFORM */}
      <BannerBeat
        testId="about-banner-workshop"
        src={IMG_WORKSHOP}
        alt="An early WearSpray prototyping workshop with hand-molded clay forms and 3D-printed band prototypes"
        position="bottom"
        lines={[{ t: "What began as a question became an engineering challenge.", accent: false }]}
        caption="Early prototyping · hand-molded forms and first prints"
      />

      <Chapter n="05" label="From an Idea to a Patented Platform" dark testId="about-patented-platform">
        <Reveal delay={0.1}>
          <div className="mt-10">
            <Bullets
              items={[
                "How do you comfortably carry liquid, lotion or gel on the wrist?",
                "How do you control dispensing?",
                "How do you make the reservoir refillable?",
                "How do you make the experience simple enough that using it feels natural?",
              ]}
            />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <Body className="mt-10">
            Those questions led to the development of WearSpray, a patented
            wearable dispensing platform that has been engineered, manufactured
            and commercially tested.
          </Body>
        </Reveal>
        <Reveal delay={0.2}>
          <Statement className="text-accent mt-10">
            The simplicity of the experience is the result of solving a
            surprisingly complex problem.
          </Statement>
        </Reveal>
      </Chapter>

      <BannerBeat
        testId="about-banner-valve"
        src={IMG_VALVE_MACRO}
        alt="Macro detail of the patented WearSpray dispensing valve seated in a band"
        position="bottom"
        lines={[{ t: "The patented dispensing valve.", accent: true }]}
        caption="Engineered detail · the heart of the platform"
      />


      {/* 06 · WHY THE WRIST? */}
      <Chapter n="06" label="Why the Wrist?" testId="about-why-wrist">
        <Reveal>
          <Heading>
            Because the wrist is already one of the most accessible places on the
            human body.
          </Heading>
        </Reveal>
        <Reveal delay={0.1}>
          <Staccato
            className="mt-12"
            lines={[
              "It travels with us.",
              "It is visible.",
              "It is reachable.",
              { t: "It is already home to one of the most important wearable technology categories in the world.", accent: true },
            ]}
          />
        </Reveal>
        <Reveal delay={0.15}>
          <Body className="mt-12">
            WearSpray began with dispensing. Its larger vision is to explore what
            happens when personal care, protection and wearable intelligence begin
            to converge.
          </Body>
        </Reveal>
        <Reveal delay={0.2}>
          <Statement className="mt-12">
            The wrist is not simply somewhere to carry a product.{" "}
            <span className="text-accent">It can become a new point of access.</span>
          </Statement>
        </Reveal>
      </Chapter>

      {/* 07 · THE FUTURE */}
      <Chapter n="07" label="The Future" dark testId="about-future">
        <Reveal>
          <Heading>Wearable technology has become remarkably good at measuring us.</Heading>
        </Reveal>
        <Reveal delay={0.1}>
          <Chips
            testId="about-measure-chips"
            items={["Activity", "Environment", "Location", "Biometrics", "Exposure"]}
          />
        </Reveal>
        <Reveal delay={0.15}>
          <Statement className="text-accent mt-14">
            The next opportunity is helping people respond.
          </Statement>
        </Reveal>
        <Reveal delay={0.2}>
          <Body className="mt-10">
            Imagine a wearable that recognizes environmental conditions, reminds
            you when action may be useful and gives you immediate access to the
            product itself.
          </Body>
          <div className="mt-8">
            <Bullets
              items={[
                "Sunscreen when UV exposure matters.",
                "Repellent when environmental conditions change.",
                "Skincare when your routine calls for it.",
                "Personal care connected to the intelligence already living on the wrist.",
              ]}
            />
          </div>
        </Reveal>
      </Chapter>

      {/* BANNER BEAT, full-bleed image with overlaid headline */}
      <BannerBeat
        testId="about-banner-measure"
        src={IMG_BLUE_VALVE}
        alt="Macro detail of the illuminated dispensing valve set into a blue WearSpray band"
        position="top"
        lines={[
          "Wearables learned to measure us.",
          { t: "The next generation may help us act.", accent: true },
        ]}
        caption="Engineered detail · built for what connects next"
      />

      {/* 08 · THE IDEA THAT STILL GUIDES US */}
      <Chapter n="08" label="The Idea That Still Guides Us" testId="about-guiding-idea">
        <Reveal>
          <Body>
            WearSpray began with a question about making something feel almost
            effortless. That idea still guides everything we are building.
          </Body>
        </Reveal>
        <Reveal delay={0.1}>
          <Staccato
            className="mt-12"
            lines={[
              "Remove the search.",
              "Remove the interruption.",
              "Remove the distance between intention and action.",
              { t: "Make personal care accessible at the moment it matters.", accent: true },
            ]}
          />
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-14">
            <span className="block font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight">
              What you need.
            </span>
            <span className="block font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight text-accent mt-1">
              When you need it.
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="mt-14 border-l-2 border-accent pl-6 md:pl-8 max-w-3xl" data-testid="about-health-beauty">
            <p className="font-body text-zinc-300 text-lg leading-relaxed">
              Because caring for your skin is caring for yourself. Protect it,
              and you keep it healthy, and healthy skin is beautiful skin.
            </p>
            <p className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight text-accent mt-5">
              Health is the new beauty.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <BrandPayoff className="mt-20" testId="about-brand-payoff-closing" />
        </Reveal>
      </Chapter>

      {/* FOUNDER SIGNATURE + BIO */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="about-founder-signature">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
            <Reveal className="md:col-span-4">
              <div className="relative max-w-[320px]">
                <div className="absolute -inset-3 radial-glow opacity-40 pointer-events-none" />
                <div className="relative border border-zinc-800 overflow-hidden bg-black" data-testid="founder-photo">
                  <img loading="lazy"
                    src={FOUNDER_IMG}
                    alt="Greg Demirjian, founder and inventor of WearSpray"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
                  <div className="absolute bottom-0 left-0 p-5">
                    <p className="font-heading text-lg font-medium text-white leading-none">Greg Demirjian</p>
                    <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-400 mt-2">
                      Founder & Inventor
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
            <div className="md:col-span-8">
              <Reveal>
                <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent block mb-6">
                  The Inventor
                </span>
                <p className="font-body text-zinc-300 text-lg leading-relaxed">
                  WearSpray is the brainchild of Greg Demirjian, an inventor and
                  storyteller whose career spans building virtual worlds,
                  holographic presentations, photography, screenwriting and music.
                </p>
                <p className="font-body text-zinc-400 leading-relaxed mt-5">
                  That blend of art and technology is exactly what a product like
                  this needs: the engineering rigor to earn a patent, plus the
                  vision to imagine where wearables, app development and human
                  protection meet. WearSpray is the result of asking a simple
                  question, what if the everyday act of protecting yourself could
                  be instant, beautiful, and always within reach?
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="flex flex-wrap gap-3 mt-10">
                  {[
                    "Virtual Worlds",
                    "Holographic Presentation",
                    "Product Innovation",
                    "App Development",
                    "Photography",
                    "Screenwriting",
                    "Music",
                    "Emerging Tech",
                  ].map((d) => (
                    <span
                      key={d}
                      className="inline-flex items-center gap-2.5 font-mono text-xs tracking-wide text-zinc-300 border border-zinc-800 bg-[#121212] px-4 py-3"
                    >
                      <span className="w-1.5 h-1.5 bg-[#00e5ff] shrink-0" />
                      {d}
                    </span>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="mt-14 pt-10 border-t border-zinc-800">
                  <p className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white">
                    Greg Demirjian
                  </p>
                  <p className="font-body text-zinc-300 text-lg mt-4">Founder, CEO, and Inventor</p>
                  <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mt-2">
                    WearSpray, Inc.
                  </p>
                  <Link
                    to="/investors"
                    data-testid="about-pitch-deck-link"
                    className="inline-flex items-center gap-2 font-body text-sm text-accent border-b border-accent/40 hover:border-accent transition-colors mt-6"
                  >
                    Considering an investment? View our pitch deck
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* THE FULL ORIGIN STORY, in the founder's own words, expandable */}
      <FounderStory />
    </div>
  );
}
